import moment from 'moment';
import styled from 'styled-components';
import { EditOutlined } from '@ant-design/icons';
import { Button, Modal, Form, Input, Select, message } from 'antd';
import { useState } from 'react';
import { StyledFormItem } from './index';
import { useAppDispatch } from '../../methods/redux/hooks';
import DeleteCommercialBank from './DeleteCommercialBank';
import { editCommercialBank } from '../../methods/redux/reducers/commercialBank';

interface IBank {
	item: any;
	setRefresh: Function;
	currencySelect: any;
}

const Style = styled.div`
	padding: 20px;
	background-color: #f8f8f8;
	margin-bottom: 10px;
	display: grid;
	justify-content: space-between;
	grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 100px;
	cursor: pointer;

	& .label {
		font-size: 13px;
		color: #808080;
		font-style: italic;
	}

	& .green {
		font-size: 13px;
		color: green;
		font-style: italic;
		padding-left: 5px;
		text-transform: capitalize;
	}

	& .red {
		font-size: 13px;
		color: red;
		font-style: italic;
		padding-left: 5px;
		text-transform: capitalize;
	}

	& .balance {
		display: flex;

		& img {
			width: 24px;
			height: 24px;
			border-radius: 50%;
			margin-right: 10px;
		}
	}
`;

const CommercialBankItem = ({ item, setRefresh, currencySelect }: IBank) => {
	const [form] = Form.useForm();
	const dispatch = useAppDispatch();

	const [isLoadingEditJob, setIsLoadingEditJob] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		form.resetFields();
	};

	const allowDepositOptions = [
		{
			value: true,
			name: 'Yes',
		},
		{
			value: false,
			name: 'No',
		},
	];
	const allowWithdrawalOptions = [
		{
			value: true,
			name: 'Yes',
		},
		{
			value: false,
			name: 'No',
		},
	];	

	const handleSubmit = async (values: any) => {
		try {
			setIsLoadingEditJob(true);
			const jobData = {
				name: values.name,
				symbol: values.symbol,
				nibssCode: values.nibssCode,
				sortCode: values.sortCode,
				localCurrencyId: values.localCurrencyId,
				address: values.address,
				countryCode: values.countryCode,
				apiTokenKeyInENV: 'string',
				allowDeposit: values.allowDeposit,
				allowWithdrawal: values.allowWithdrawal,
			};

			await dispatch(editCommercialBank({ id: item._id, data: jobData }));
			handleCancel();
			form.resetFields();
			setRefresh(Math.random());
		} catch (error) {
			message.error('Cnat create');
		} finally {
			setIsLoadingEditJob(false);
		}
	};
	return (
		<div>
			<Style>
				<div>{item?.name}</div>
				<div>{item?.localCurrencyId?.symbol}</div>
				<div>{item?.countryCode}</div>
				<div>{item?.nibssCode}</div>
				<div>{item?.address}</div>
				<div>
					<p>
						{item.createdAt
							? moment(item.createdAt).format('MMM DD, YY / hh:mm:ss a')
							: '--'}
					</p>
				</div>
				<div>
					<p className={item?.status === 'active' ? 'green' : 'red'}>
						{item?.status}
					</p>
				</div>
				<div>
					<Button
						size="small"
						// style={{ fontSize: '12px', width: 'content-fit' }}
						onClick={showModal}
					>
						<EditOutlined className="deleteIcon" />
					</Button>

					<DeleteCommercialBank id={item?._id} setRefresh={setRefresh} />
				</div>
			</Style>

			{/* Modal for Editing a Commercial Bank */}
			<Modal
				title={`Edit ${item?.name}`}
				visible={isModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleSubmit}>
					<StyledFormItem
						name="name"
						label="Bank name"
						rules={[{ required: true, message: 'Please input the bank name!' }]}
						initialValue={item?.name}
					>
						<Input placeholder="e.g., United Bank for Africa" />
					</StyledFormItem>
					<StyledFormItem
						name="symbol"
						label="Bank symbol"
						rules={[
							{ required: true, message: 'Please input the bank symbol!' },
						]}
						initialValue={item?.localCurrencyId?.symbol}
					>
						<Input placeholder="e.g., UBA" />
					</StyledFormItem>
					<StyledFormItem
						name="nibssCode"
						label="Nibss Code"
						rules={[
							{ required: true, message: 'Please input the Nibss code!' },
						]}
						initialValue={item?.nibssCode}
					>
						<Input placeholder="e.g., 222" />
					</StyledFormItem>
					<StyledFormItem
						name="sortCode"
						label="Sort Code"
						rules={[{ required: true, message: 'Please input the sortCode!' }]}
						initialValue={item?.sortCode}
					>
						<Input placeholder="e.g., 222" />
					</StyledFormItem>
					<StyledFormItem
						name="localCurrencyId"
						label="Local Currency Id"
						rules={[
							{ required: true, message: 'Please input the localCurrencyId!' },
						]}
						initialValue={item?.localCurrencyId}
					>
						<Select
							placeholder="Select a currency"
							value={item?.localCurrencyId}
							options={currencySelect}
						/>
					</StyledFormItem>
					<StyledFormItem
						name="address"
						label="Address"
						rules={[
							{ required: true, message: 'Please input the bank address!' },
						]}
						initialValue={item?.address}
					>
						<Input placeholder="e.g., Nigeria" />
					</StyledFormItem>
					<StyledFormItem
						name="countryCode"
						label="Country Code"
						rules={[
							{ required: true, message: 'Please input a country code!' },
						]}
						initialValue={item?.countryCode}
					>
						<Input placeholder="e.g., NG" />
					</StyledFormItem>

					<StyledFormItem
						name="allowDeposit"
						label="Allow Deposit"
						rules={[
							{ required: true, message: 'Please input allow deposit option' },
						]}
						initialValue={item?.allowDeposit}
					>
						<Select style={{ width: '100%' }} placeholder="Select an option">
							{allowDepositOptions?.map((option: any) => (
								<Select.Option value={option.value} key={option.value}>
									{option.name}
								</Select.Option>
							))}
						</Select>
					</StyledFormItem>

					<StyledFormItem
						name="allowWithdrawal"
						label="Allow Withdrawal"
						rules={[
							{
								required: true,
								message: 'Please input allow withdrawal option',
							},
						]}
						initialValue={item?.allowWithdrawal}
					>
						<Select style={{ width: '100%' }} placeholder={item?.allowWithdrawal}>
							{allowDepositOptions?.map((option: any) => (
								<Select.Option value={option.value} key={option.value}>
									{option.name}
								</Select.Option>
							))}
						</Select>
					</StyledFormItem>
					<Form.Item>
						<Button type="primary" htmlType="submit" loading={isLoadingEditJob}>
							{isLoadingEditJob ? 'Editing...' : 'Edit Commercial Bank'}
						</Button>
						<Button onClick={handleCancel} style={{ marginLeft: '8px' }}>
							Cancel
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default CommercialBankItem;
